import { type Signal, type ReadonlySignal, useSignal } from '@preact/signals'
import { Role } from 'core'
import { useCallback } from 'preact/hooks'

import {
	Button,
	Input,
	Modal,
	Select,
	TextLink,
	Typography
} from 'components/UI'

interface Props {
	addCollaborator: () => void
	addOtherCollaborator: () => void
	bannerErrorMessage?: string
	closeAddCollaboratorModal: () => void
	collaboratorEmail: Signal<string>
	displaySuccessContent: boolean
	inputErrors: ReadonlySignal<Record<string, string | undefined>>
	isAddCollaboratorButtonDisabled: boolean
	isModalOpen: boolean
	projectName: string
	rolesState: ReadonlySignal<Role[]>
	rolSelectedState: ReadonlySignal<string>
	selectCollaboratorRole: (_: string, value: string) => void
	updateCollaboratorEmail: (value: string) => void
}

const AddCollaboratorModal = ({
	addCollaborator,
	addOtherCollaborator,
	bannerErrorMessage,
	closeAddCollaboratorModal,
	collaboratorEmail,
	displaySuccessContent,
	inputErrors,
	isAddCollaboratorButtonDisabled,
	isModalOpen,
	projectName,
	rolesState,
	rolSelectedState,
	selectCollaboratorRole: onSelectCollaboratorRole,
	updateCollaboratorEmail
}: Props) => {
	const SuccesContent = (
		<>
			<div class='mt-10'>
				<p class='transition-text-opacity inline-block font-inter text-[0.875rem] font-normal leading-[1.225rem] text-black-200 text-opacity-100 duration-300'>
					<span class='text-product'>{collaboratorEmail} </span>
					has been added as a collaborator to the project.
				</p>
			</div>

			<div class='modal-footer mt-10 flex items-center justify-between'>
				<button onClick={addOtherCollaborator} type='button'>
					<span class='text-sm font-normal text-black-200 underline'>
						Add Other Collaborators
					</span>
				</button>

				<Button onClick={closeAddCollaboratorModal} text='Close' />
			</div>
		</>
	)

	const creatingCollaborator = useSignal(false)

	const handleCreateCollaborator = useCallback(
		async (e: Event) => {
			e.preventDefault()

			creatingCollaborator.value = true
			await addCollaborator()
			creatingCollaborator.value = false
		},
		// eslint-disable-next-line react-hooks/exhaustive-deps
		[]
	)

	const AddCollaboratorContent = (
		<form onSubmit={handleCreateCollaborator}>
			<div class='mt-10'>
				<Input
					error={inputErrors.value?.userHandler}
					hasError={!!inputErrors.value?.userHandler}
					id='collaborator-username'
					label='Email'
					name='userHandler'
					onInput={updateCollaboratorEmail}
					placeholder='Write the email of the collaborator'
					value={collaboratorEmail}
				/>
			</div>
			<div class='mt-4'>
				<Select
					description='Assign a Role'
					id='role'
					label='Role'
					onChange={onSelectCollaboratorRole}
					options={rolesState.value}
					value={rolSelectedState.value}
				/>
			</div>
			<div class='modal-footer mt-10 flex justify-between'>
				<TextLink
					ariaLabel='Close modal'
					onClick={closeAddCollaboratorModal}
					size='small'
					text='Cancel'
					variant='secondary'
				/>

				<Button
					disabled={isAddCollaboratorButtonDisabled}
					isLoading={creatingCollaborator.value}
					text='Add Collaborator'
					type='submit'
				/>
			</div>
		</form>
	)

	return (
		<Modal
			bannerErrorMessage={bannerErrorMessage}
			className='p-10'
			isOpen={isModalOpen}
			onCloseModal={closeAddCollaboratorModal}
		>
			<Typography
				class='inline-block'
				color='text-white'
				text='Add a collaborator to'
				variant='h2'
			/>
			<Typography
				class='ml-1 inline-block capitalize'
				color='text-product'
				text={projectName}
				variant='h2'
			/>

			{displaySuccessContent ? SuccesContent : AddCollaboratorContent}
		</Modal>
	)
}

export { AddCollaboratorModal }
